import React, { FC, useEffect, useRef, useState } from 'react';
import { Product, Variant } from 'shared/types/product';

interface ColorSwatchProps {
  variants: Variant[];
  product?: Product;
  setSelectedVariant?: (variant: Variant) => void;
  viewMode?: string;
}

const ColorSwatchesDropdown: FC<ColorSwatchProps> = ({ product, variants, setSelectedVariant, viewMode }) => {
  const [variantsToUse, setVariantsToUse] = useState<Variant[]>([]);
  const [selectedVariant, setSelected] = useState<Variant | null>(
    product?.selectedVariant ?? null
  );
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (product?.selectedVariant) {
      setSelected(product.selectedVariant);
    }
  }, [product?.selectedVariant?.id, product?.selectedVariant?.sku, product?.selectedVariant]);


  const isNotApplicable = (val?: string | null) => {
    if (!val || typeof val !== 'string') return false;
    const lower = val.trim().toLowerCase();
    return lower === 'not applicable' || lower === 'not-applicable' || lower === 'n/a';
  };

  // Helper function to get variant name
  const getVariantName = (variant: Variant | null) => {
    
    if (!variant?.attributes) return null;
    
    // If variant-name exists, use it (this will now work since we're fetching it)
    if (variant.attributes['variant-name']) {
      if (isNotApplicable(variant.attributes['variant-name'])) return null;
      return variant.attributes['variant-name'];
    }
    
    // Fallback: create variant-name from available data
    const colour = variant.attributes.colour;
    const itemCode = variant.attributes['item-code'];
    
    if (colour && !isNotApplicable(colour)) {
      if (itemCode) {
        return `${colour} - ${itemCode}`;
      }
      return colour;
    } else if (itemCode && !isNotApplicable(colour)) {
      return itemCode;
    }
    
    return null;
  };



  useEffect(() => {
    // on existing plp page showing only unique colors

    const filteredVariants = (variants || []).filter(
      (variant) =>
        !isNotApplicable(variant?.attributes?.colour) &&
        !isNotApplicable(variant?.attributes?.['variant-name']) &&
        getVariantName(variant)
    );
    setVariantsToUse(filteredVariants);

    if (filteredVariants.length <= 1) {
      setIsDisabled(true);
    }
  }, [variants]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (variant: Variant) => {
    setSelected(variant);
    if (setSelectedVariant) {
      setSelectedVariant(variant);
    }
    setIsOpen(false);
  };

  const variantName = getVariantName(selectedVariant);
  if (!variantName) {
    return null;
  }

  return (
    <div className={`relative ${viewMode == 'list' ? 'w-full' : ''}`} ref={dropdownRef}>

      {getVariantName(selectedVariant) && (
        <div
          className={`flex items-center gap-5 p-4 border rounded-md bg-white shadow-sm relative ${!isDisabled ? "cursor-pointer" : "cursor-default" } w-full `}
          onClick={() => !isDisabled && setIsOpen(!isOpen)}
        >
          <img
            // src={`/images/colorswatches/swatch_${getVariantName(selectedVariant).replaceAll(' ', '').replaceAll('/', '')}.png`}
            src={`/images/colorswatches/swatch_${selectedVariant?.attributes?.colour.replaceAll(' ', '').replaceAll('/', '')}.png`}
            alt={getVariantName(selectedVariant)}
            className="w-16 h-16 rounded-full mr-2"
          />
          
          <div className="flex items-center justify-between w-full gap-2">
            <span className="text-gray-700 text-12 break-words whitespace-normal">
              {getVariantName(selectedVariant) || "Select a Color"}
            </span>

            {!isDisabled && (
              <svg
                className="w-18 h-18 flex-shrink-0 transition text-gray-600 cursor-pointer"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>

        </div>
      )}

      {isOpen && (
        <div className="absolute w-full mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-10">
          {variantsToUse.map((variant) => (
            <div
              key={variant.id}
              className={`flex items-center p-2 gap-5 cursor-pointer 
                ${selectedVariant?.id === variant.id ? "bg-blue-100 font-semibold" : "hover:bg-gray-100"}`}
              onClick={() => handleSelect(variant)}
            >
              <img
                src={`/images/colorswatches/swatch_${variant.attributes?.colour.replaceAll(' ', '').replaceAll('/', '')}.png`}
                alt={variant.attributes?.colour}
                className="w-16 h-16 rounded-full mr-2"
              />
              {/*<span className="text-gray-700">{variant.attributes?.colour}</span> */}
              <span className="text-gray-700 text-14">{getVariantName(variant)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default ColorSwatchesDropdown;